/**
 * LA FICHA DEL DUENO COMO FORMULARIO POR PASOS (ONB-FICHA-FORMULARIO-T1, D-37 = A).
 *
 * DE DONDE SALE. Josse, 17-sep: la ficha como chat guionado no es conversacion real, en el
 * iPhone salio caotica (burbujas fuera del borde, Enviar cortado, media pantalla vacia, el
 * PIN repetido) y al final no mostraba un resumen para verificar. El 21-sep eligio la opcion A
 * de la maqueta: 4 pasos (tu negocio / donde esta / tus datos / autorizaciones), resumen
 * editable y el PIN con la regla dicha ANTES de pedirlo.
 *
 * LO QUE NO CAMBIA: los mismos endpoints (GET token, POST responses por campo, POST pin), los
 * mismos 12 campos, la regla del API para correo y telefonos (contactoDelNegocio.js), el
 * telefono del negocio con -es el mismo celular?-, los tipos del catalogo, la factura
 * electronica solo si la API la ofrece, y el PIN por su propio camino (onboardPin.js).
 *
 * Argemiro sigue siendo la voz de WhatsApp; aqui solo saluda y dice quien revisa.
 *
 * Sin DOM: lo prueba scripts/check-formulario-onboard.mjs.
 */

import { DEPARTAMENTOS, ciudadesDe } from '../negocios/colombia.js';
import { problemaDeLaRespuesta, sinClavesLocales, telefonoValido } from './contactoDelNegocio.js';

/** Los 12 campos que cuenta el progreso y que el API exige para completar la sesion. */
export const CAMPOS_REQUERIDOS = [
  'displayName', 'legalName', 'taxId', 'businessType',
  'addressLine1', 'department', 'city',
  'ownerName', 'ownerId', 'ownerPhone', 'ownerEmail',
  'horario'
];

/**
 * Los consentimientos. Consentir es MARCAR una casilla que empieza sin marcar: nada se da por
 * aceptado. El de datos (Ley 1581) y los terminos son obligatorios; la factura electronica solo
 * aparece si la API dice que se ofrece (consentimientosQueAplican, ONB-DIAN-PARAMETRO-T1).
 */
export const CONSENTIMIENTOS = [
  { field: 'consentDatos', required: true, etiqueta: 'Autorizo el tratamiento de mis datos personales según la Ley 1581 de 2012' },
  { field: 'consentTerminos', required: true, etiqueta: 'Acepto los Términos y Condiciones de Nexo Digital 360' },
  { field: 'consentFacturaElectronica', required: false, etiqueta: 'Quiero activar la facturación electrónica DIAN (se puede cambiar después)' }
];

/** La regla del PIN, dicha ANTES de pedirlo (D-37). La que decide es la del API. */
export const REGLA_DEL_PIN = 'Tu PIN son 4 números. No pueden ser todos iguales (como 0000) ni seguidos (como 1234 o 4321).';

export const OTRA_CIUDAD = '__otra__';

const lleno = (v) => v !== null && v !== undefined && String(v).trim() !== '';
const digitos = (v) => String(v ?? '').replace(/\D/g, '');

// ONB-FICHA-REGISTRO-T1 (Josse 8-oct, 5 capturas sobre Demo Nexo 1.5): la ficha se llena sin tropiezos.
/** Sin tildes ni mayusculas, para comparar lo que se escribe contra la lista ("medellin" = "Medellín"). */
const plano = (s) => String(s ?? '').normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
const alfabetico = (a, b) => String(a).localeCompare(String(b), 'es', { sensitivity: 'base' });

/** (1) Los tipos de negocio en orden alfabetico, con Otro al final. */
export function ordenarTipos(tipos) {
  const lista = [...(tipos || [])];
  const otros = lista.filter((t) => plano(t) === 'otro');
  return lista.filter((t) => plano(t) !== 'otro').sort(alfabetico).concat(otros);
}

/** (2) Los municipios del departamento, en orden alfabetico y SIN agregar lo escrito (eso no es la lista). */
export function municipiosDe(departamento) {
  return ciudadesDe(departamento, '').sort(alfabetico);
}

/** (2) El municipio canon que corresponde a lo escrito (sin tildes ni mayusculas), o null si no esta en la lista. */
export function municipioCanon(departamento, escrito) {
  const buscado = plano(escrito);
  if (!buscado) return null;
  return municipiosDe(departamento).find((m) => plano(m) === buscado) || null;
}

/** (3) Las plantillas del horario. El campo horario sigue siendo un TEXTO legible, como antes. */
export const PLANTILLAS_HORARIO = [
  { id: 'lun-sab', etiqueta: 'Lunes a sábado', dias: 'Lun-Sáb' },
  { id: 'lun-vie', etiqueta: 'Lunes a viernes', dias: 'Lun-Vie' },
  { id: 'todos', etiqueta: 'Todos los días', dias: 'Todos los días' },
  { id: 'personalizado', etiqueta: 'Otro horario (lo escribo)', dias: null }
];

/** "08:00" -> "8am", "20:30" -> "8:30pm", "12:00" -> "12pm". null si no es una hora. */
export function horaLegible(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  const sufijo = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return h12 + (min ? ':' + String(min).padStart(2, '0') : '') + sufijo;
}

/** (3) El texto del horario que viaja: "Lun-Sáb 8am-8pm". null si falta algo; personalizado devuelve null (va el texto escrito). */
export function textoDelHorario({ plantilla, abre, cierra } = {}) {
  const p = PLANTILLAS_HORARIO.find((x) => x.id === plantilla);
  if (!p || !p.dias) return null;
  const a = horaLegible(abre), c = horaLegible(cierra);
  if (!a || !c || abre === cierra) return null;
  return `${p.dias} ${a}-${c}`;
}

/** (3) Lo inverso: "Lun-Sáb 8am-8pm" (escrito por esta ficha) -> { plantilla, abre, cierra }; otro texto -> null. */
export function leerHorario(texto) {
  const m = /^(.+) (\d{1,2})(?::(\d{2}))?(am|pm)-(\d{1,2})(?::(\d{2}))?(am|pm)$/.exec(String(texto ?? '').trim());
  const p = m && PLANTILLAS_HORARIO.find((x) => x.dias === m[1]);
  if (!p) return null;
  const a24 = (h, min, s) => String((Number(h) % 12) + (s === 'pm' ? 12 : 0)).padStart(2, '0') + ':' + (min || '00');
  return { plantilla: p.id, abre: a24(m[2], m[3], m[4]), cierra: a24(m[5], m[6], m[7]) };
}

/** Las marcas de pantalla del horario: nunca viajan a la API (cityOtra tiene su propia linea abajo). */
const CLAVES_DEL_HORARIO = ['horarioPlantilla', 'horarioAbre', 'horarioCierra'];

/**
 * ONB-RAZON-SOCIAL-AMABLE-T1 (Josse 15-sep): casi siempre son negocios pequenos, a veces sin registro. Primero se pregunta
 * si el negocio esta registrado con otro nombre; si NO, no se pregunta razon social ni NIT: el nombre legal es el nombre
 * del negocio y el documento es la cedula del dueno. La marca es de pantalla y no viaja: la API sigue recibiendo
 * legalName y taxId (QUE HACE A: nada).
 */
export const CLAVE_REGISTRADO = 'registradoConOtroNombre';
const noRegistrado = (v) => v?.[CLAVE_REGISTRADO] === false;

/** Lo escrito tal como se ENVIA y se MUESTRA: la ciudad -otra- escrita, el municipio canon y el horario de la plantilla. */
function loEscrito(valores) {
  const v = { ...(valores || {}) };
  if (v.city === OTRA_CIUDAD && lleno(v.cityOtra)) v.city = v.cityOtra;
  else if (lleno(v.city) && v.city !== OTRA_CIUDAD) v.city = municipioCanon(v.department, v.city) || v.city;
  if (v.horarioPlantilla && v.horarioPlantilla !== 'personalizado') {
    v.horario = textoDelHorario({ plantilla: v.horarioPlantilla, abre: v.horarioAbre, cierra: v.horarioCierra }) || '';
  }
  // Sin registro: el nombre legal es el del negocio y el documento es la cedula del dueno.
  if (noRegistrado(v)) {
    v.legalName = lleno(v.displayName) ? String(v.displayName).trim() : '';
    v.taxId = lleno(v.ownerId) ? String(v.ownerId).trim() : '';
  }
  return v;
}

/** Los 4 pasos, con las opciones que llegan del catalogo y los consentimientos que aplican. */
export function pasosDelFormulario({ tipos = [], consentimientos = [] } = {}) {
  return [
    { id: 'negocio', titulo: 'Tu negocio', campos: [
      { clave: 'displayName', etiqueta: 'Nombre del negocio', ayuda: 'El nombre con el que te conocen', tipo: 'text', requerido: true, autocomplete: 'organization' },
      { clave: CLAVE_REGISTRADO, etiqueta: '¿Tu negocio está registrado con otro nombre?', ayuda: 'Por ejemplo, una empresa con NIT. Si no, usamos el nombre del negocio y tu cédula.', tipo: 'sino', requerido: true },
      { clave: 'legalName', etiqueta: 'Nombre registrado', ayuda: 'Como aparece en el RUT', tipo: 'text', requerido: true, cuando: (v) => v[CLAVE_REGISTRADO] === true },
      { clave: 'taxId', etiqueta: 'NIT', ayuda: 'El número que aparece en el RUT', tipo: 'text', inputmode: 'numeric', requerido: true, cuando: (v) => v[CLAVE_REGISTRADO] === true },
      { clave: 'businessType', etiqueta: 'Tipo de negocio', tipo: 'select', opciones: ordenarTipos(tipos), requerido: true }
    ] },
    { id: 'lugar', titulo: 'Dónde está', campos: [
      { clave: 'addressLine1', etiqueta: 'Dirección', ayuda: 'Calle, número y barrio', tipo: 'text', requerido: true, autocomplete: 'street-address' },
      { clave: 'department', etiqueta: 'Departamento', tipo: 'select', opciones: DEPARTAMENTOS, requerido: true },
      { clave: 'city', etiqueta: 'Ciudad o municipio', tipo: 'ciudad', requerido: true },
      { clave: 'horario', etiqueta: 'Horario de atención', ayuda: 'Elige los días y la hora en que abres y cierras', tipo: 'horario', requerido: true }
    ] },
    { id: 'dueno', titulo: 'Tus datos', campos: [
      { clave: 'ownerName', etiqueta: 'Tu nombre completo', tipo: 'text', requerido: true, autocomplete: 'name' },
      { clave: 'ownerId', etiqueta: 'Cédula', tipo: 'text', inputmode: 'numeric', requerido: true },
      { clave: 'ownerPhone', etiqueta: 'Celular', ayuda: 'Por aquí te escribe Argemiro por WhatsApp', tipo: 'tel', inputmode: 'tel', requerido: true, autocomplete: 'tel' },
      { clave: 'mismoCelular', etiqueta: '¿El teléfono del negocio es ese mismo celular?', tipo: 'sino', requerido: true },
      { clave: 'businessPhone', etiqueta: 'Teléfono del negocio', ayuda: 'Fijo o celular; si es fijo, con indicativo', tipo: 'tel', inputmode: 'tel', requerido: true, cuando: (v) => v.mismoCelular === false },
      // Como en el chat: se pregunta siempre que haya telefono del negocio, sea el mismo celular o no.
      { clave: 'businessPhoneHasWhatsapp', etiqueta: '¿El número del negocio tiene WhatsApp?', tipo: 'sino', requerido: true, cuando: (v) => typeof v.mismoCelular === 'boolean' },
      { clave: 'ownerEmail', etiqueta: 'Correo del propietario', tipo: 'email', inputmode: 'email', requerido: true, autocomplete: 'email' }
    ] },
    { id: 'autoriza', titulo: 'Autorizaciones', campos: consentimientos.map((c) => (
      { clave: c.field, etiqueta: c.etiqueta || c.field, tipo: 'check', requerido: c.required === true }
    )) }
  ];
}

/** Los campos de un paso que aplican con lo que va escrito. */
export function camposVisibles(paso, valores) {
  return (paso?.campos || []).filter((c) => typeof c.cuando !== 'function' || c.cuando(valores || {}));
}

/**
 * Lo que llega del state del token (WhatsApp, alta rapida, o una visita anterior), listo para
 * precargar: lo conocido aparece lleno y se confirma, no se vuelve a preguntar.
 */
export function precarga(capturado) {
  const v = { ...(capturado || {}) };
  if (typeof v.mismoCelular !== 'boolean' && lleno(v.businessPhone)) {
    v.mismoCelular = lleno(v.ownerPhone) && digitos(v.businessPhone) === digitos(v.ownerPhone);
  }
  for (const c of CONSENTIMIENTOS) if (v[c.field] !== true) delete v[c.field];
  // ONB-RAZON-SOCIAL-AMABLE-T1: lo que ya vino dice si esta registrado. Nombre legal igual al del negocio y documento igual
  // a la cedula = sin registro; un nombre legal distinto = registrado. Si no hay como saberlo, se pregunta.
  if (typeof v[CLAVE_REGISTRADO] !== 'boolean' && lleno(v.legalName)) {
    const mismoNombre = plano(v.legalName) === plano(v.displayName);
    const mismoDocumento = !lleno(v.taxId) || (lleno(v.ownerId) && digitos(v.taxId) === digitos(v.ownerId));
    v[CLAVE_REGISTRADO] = !(mismoNombre && mismoDocumento);
  }
  // El municipio que vino escrito ("medellin") llega como el de la lista; uno que no esta se deja
  // para que el paso lo senale, no se acepta en silencio.
  if (lleno(v.city) && v.city !== OTRA_CIUDAD) v.city = municipioCanon(v.department, v.city) || v.city;
  // El horario que ya vino: si lo escribio esta ficha se vuelve plantilla; si no, queda como texto propio.
  if (!v.horarioPlantilla && lleno(v.horario)) {
    const h = leerHorario(v.horario);
    if (h) { v.horarioPlantilla = h.plantilla; v.horarioAbre = h.abre; v.horarioCierra = h.cierra; }
    else v.horarioPlantilla = 'personalizado';
  }
  return v;
}

/** Los errores de un paso: { clave: frase }. Vacio = se puede seguir. */
export function erroresDelPaso(paso, valores) {
  const v = valores || {};
  const errores = {};
  for (const c of camposVisibles(paso, v)) {
    const valor = v[c.clave];
    if (c.tipo === 'check') {
      if (c.requerido && valor !== true) errores[c.clave] = 'Sin esta autorización no podemos registrar tu negocio (es un requisito legal).';
      continue;
    }
    if (c.tipo === 'sino') {
      if (c.requerido && typeof valor !== 'boolean') errores[c.clave] = 'Elige sí o no.';
      continue;
    }
    if (c.tipo === 'ciudad' && valor === OTRA_CIUDAD) {
      if (!lleno(v.cityOtra)) errores[c.clave] = 'Escribe tu ciudad o municipio.';
      continue;
    }
    if (c.tipo === 'ciudad' && lleno(valor) && municipiosDe(v.department).length && !municipioCanon(v.department, valor)) {
      errores[c.clave] = `No encontramos "${String(valor).trim()}" en ${v.department}. Elígelo de la lista o toca "Mi municipio no está en la lista".`;
      continue;
    }
    if (c.tipo === 'horario' && v.horarioPlantilla && v.horarioPlantilla !== 'personalizado') {
      if (!textoDelHorario({ plantilla: v.horarioPlantilla, abre: v.horarioAbre, cierra: v.horarioCierra })) {
        errores[c.clave] = 'Elige a qué hora abres y a qué hora cierras.';
      }
      continue;
    }
    if (c.requerido && (!lleno(valor) || valor === OTRA_CIUDAD)) { errores[c.clave] = 'Falta este dato.'; continue; }
    const problema = problemaDeLaRespuesta(c.clave, String(valor ?? ''));
    if (problema) errores[c.clave] = problema;
  }
  return errores;
}

/**
 * El primer paso con algo por completar; pasos.length = todo listo, va el resumen. `confirmados` (CLI-ACT-T1): claves que
 * el cliente deja como estan en la ficha; no cuentan como pendientes (la API las toma de la ficha).
 */
export function primerPasoPendiente(pasos, valores, confirmados = null) {
  const i = pasos.findIndex((p) => Object.keys(erroresDelPaso(p, valores)).some((k) => !confirmados?.has(k)));
  return i === -1 ? pasos.length : i;
}

/** El paso donde vive una clave (para volver a el cuando el API rechaza una respuesta). */
export function pasoDeLaClave(pasos, clave) {
  return pasos.findIndex((p) => p.campos.some((c) => c.clave === clave));
}

/**
 * Lo que viaja a POST responses: sin marcas locales (mismoCelular), el telefono del negocio
 * copiado del celular cuando es el mismo, los si/no como booleanos y sin textos vacios.
 * Nunca el PIN: el PIN no pasa por aqui.
 */
export function cuerpoDeRespuestas(valores, claves = null) {
  const v = loEscrito(valores);
  if (v.mismoCelular === true && lleno(v.ownerPhone)) v.businessPhone = v.ownerPhone;
  const limpio = sinClavesLocales(v);
  const out = {};
  for (const [k, val] of Object.entries(limpio)) {
    // Con `claves` (guardar un paso) solo viaja lo de ese paso; el telefono del negocio va con
    // su pregunta -es el mismo celular?-, que es la que lo decide.
    // Sin registro, el nombre legal viaja con el nombre del negocio y el documento con la cedula del dueno (paso 3).
    const derivado = noRegistrado(v) && ((k === 'legalName' && claves?.includes('displayName')) || (k === 'taxId' && claves?.includes('ownerId')));
    if (claves && !claves.includes(k) && !(k === 'businessPhone' && claves.includes('mismoCelular')) && !derivado) continue;
    // cityOtra es de la pantalla (la ciudad escrita ya viaja en city); el PIN nunca pasa por aqui.
    if (k === 'pin' || k === 'Pin' || k === 'cityOtra' || k === CLAVE_REGISTRADO) continue;
    if (CLAVES_DEL_HORARIO.includes(k)) continue;
    if (typeof val === 'boolean') { out[k] = val; continue; }
    if (lleno(val) && val !== OTRA_CIUDAD) out[k] = String(val).trim();
  }
  return out;
}

/** El resumen para verificar antes de enviar: [{ paso, indice, filas: [[etiqueta, valor]] }]. */
export function resumen(pasos, valores) {
  const v = loEscrito(valores);
  return pasos.map((p, indice) => ({
    paso: p.titulo,
    indice,
    filas: camposVisibles(p, v).map((c) => {
      const val = v[c.clave];
      const legible = typeof val === 'boolean' ? (val ? 'Sí' : 'No') : (lleno(val) && val !== OTRA_CIUDAD ? String(val).trim() : '—');
      return [c.etiqueta, legible];
    })
  }));
}

/** Los municipios del departamento elegido, en orden alfabetico (la lista que autocompleta). */
export function ciudadesPara(valores) {
  return municipiosDe(valores?.department);
}

/** Un telefono escrito vale? (la misma regla del API, reexportada para la vista). */
export { telefonoValido };
